import { useState, useEffect, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import { setStoredTimeout } from "../hooks/useSessionTimeout";
import Sidebar from "../components/Sidebar";
import {
  Topbar,
  StatCard,
  EventsTable,
  FilesTable,
  ConnectorsPanel,
  StoragePanel,
  PageError,
  friendlyError,
} from "../components";

export default function Dashboard() {
  const { get, post } = useApi();

  const [jobs, setJobs]           = useState<Job[]>([]);
  const [tenant, setTenant]       = useState<Tenant | null>(null);
  const [usage, setUsage]         = useState<Usage | null>(null);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [activeTab, setActiveTab] = useState<"events" | "files">("events");
  const [triggering, setTriggering] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Load initial data ────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      get<{ jobs: Job[]; total: number }>("/api/jobs?limit=20"),
      get<{ tenant: Tenant; user: User; usage: Usage }>("/api/tenants/me"),
      get<{ connectors: Connector[] }>("/api/connectors"),
      get<{ schedules: Schedule[] }>("/api/schedules"),
    ]).then(([jobsRes, tenantRes, connRes, schedRes]) => {
      setJobs(jobsRes.jobs);
      setTenant(tenantRes.tenant);
      setUsage(tenantRes.usage ?? null);
      if (tenantRes.tenant.session_timeout_mins) {
        setStoredTimeout(tenantRes.tenant.session_timeout_mins);
      }
      setConnectors(connRes.connectors);
      setSchedules(schedRes.schedules);
    }).catch(err => setError(friendlyError(err)));
  }, [get]);

  // ── Poll for running jobs ────────────────────────────────────────────────
  useEffect(() => {
    const hasRunning = jobs.some((j) => j.status === "running");
    if (!hasRunning) return;
    const interval = setInterval(async () => {
      const res = await get<{ jobs: Job[] }>("/api/jobs?limit=20");
      setJobs(res.jobs);
      if (!res.jobs.some((j) => j.status === "running")) clearInterval(interval);
    }, 3000);
    return () => clearInterval(interval);
  }, [jobs, get]);

  // ── Refresh schedules every 60s so next_run_at stays current ─────────────
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await get<{ schedules: Schedule[] }>("/api/schedules");
        setSchedules(res.schedules);
      } catch { /* silent — stale data is acceptable */ }
    }, 60_000);
    return () => clearInterval(interval);
  }, [get]);

  // ── Countdown to next scheduled backup ──────────────────────────────────
  useEffect(() => {
    const active = schedules.find(s => s.is_active && s.next_run_at);
    const next = active?.next_run_at;
    if (!next) { setCountdown(0); return; }
    let refreshed = false; // only refresh once when countdown hits 0
    const update = async () => {
      const diff = Math.floor((new Date(next).getTime() - Date.now()) / 1000);
      setCountdown(Math.max(0, diff));
      if (diff <= 0 && !refreshed) {
        // next_run_at is in the past — refresh once, then let the 60s poll handle it
        refreshed = true;
        try {
          const res = await get<{ schedules: Schedule[] }>("/api/schedules");
          setSchedules(res.schedules);
        } catch { /* ignore */ }
      }
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [schedules, get]);

  // ── Trigger manual backup ────────────────────────────────────────────────
  const triggerBackup = useCallback(async () => {
    if (!connectors[0] || triggering) return;
    setTriggering(true);
    try {
      const res = await post<{ job: Job }>("/api/jobs", {
        connector_id: connectors[0].id,
      });
      setJobs((prev) => [res.job, ...prev]);
    } finally {
      setTriggering(false);
    }
  }, [connectors, triggering, post]);

  // ── Derived stats ────────────────────────────────────────────────────────
  const completedJobs   = jobs.filter((j) => j.status === "complete");
  const lastJob         = completedJobs[0];
  const totalBytes      = usage?.total_bytes_all_time ?? 0;
  const lastJobBytes    = usage?.latest_job_bytes ?? 0;
  const lastJobRecords  = usage?.latest_job_records
    ? Object.values(usage.latest_job_records).reduce((a, b) => a + b, 0)
    : 0;
  const totalDataTypes  = usage?.latest_job_records
    ? Object.keys(usage.latest_job_records).length
    : 0;
  const fmtBytes        = formatBytes(totalBytes);
  const lastTime        = usage?.latest_job_at
    ? new Date(usage.latest_job_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : lastJob
      ? new Date(lastJob.completed_at!).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      : "—";
  const countdownFmt    = formatCountdown(countdown);

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar
          title="Overview"
          subtitle={`Last updated: ${new Date().toLocaleTimeString("en-GB")}`}
          onBackup={triggerBackup}
          backupLoading={triggering}
        />
        <main className="page">

          {error && <PageError message={error} onRetry={() => window.location.reload()} />}

          {!error && (<>
          {/* ── Next backup banner ── */}
          {(() => {
            const activeSchedule = schedules.find(s => s.is_active && s.next_run_at);
            if (activeSchedule) {
              return (
                <div className="banner">
                  <div className="banner-left">
                    <span className="pulse" />
                    <span className="banner-text">
                      Next scheduled backup in <strong>{countdownFmt}</strong>
                    </span>
                  </div>
                  <div className="banner-right">
                    <span className="banner-timer">
                      {activeSchedule.cron_expression} UTC
                    </span>
                    <button
                      className="btn btn-primary"
                      onClick={triggerBackup}
                      disabled={triggering}
                    >
                      {triggering ? "Running…" : "Backup now"}
                    </button>
                  </div>
                </div>
              );
            }
            // No active schedule
            return (
              <div className="banner" style={{ borderColor: "var(--border2)" }}>
                <div className="banner-left">
                  <span className="banner-text" style={{ color: "var(--text3)" }}>
                    Backup schedule not enabled
                  </span>
                </div>
                <div className="banner-right">
                  <button
                    className="btn btn-primary"
                    onClick={triggerBackup}
                    disabled={triggering}
                  >
                    {triggering ? "Running…" : "Backup now"}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ── Stat cards ── */}
          <div className="stat-grid">
            <StatCard
              accent="teal"
              label="Total backed up"
              value={fmtBytes}
              sub={`${(usage?.jobs_last_30d ?? 0).toLocaleString()} runs in last 30 days`}
              icon="storage"
            />
            <StatCard
              accent="amber"
              label="Last backup"
              value={lastTime}
              sub={
                usage?.latest_job_at ? (
                  <span>
                    {usage.latest_job_status === "failed"  && <span className="status-error">● failed</span>}
                    {usage.latest_job_status === "running" && <span className="status-warn">● running</span>}
                    {usage.latest_job_status === "queued"  && <span className="status-warn">● queued</span>}
                    {(usage.latest_job_status === "complete" || !usage.latest_job_status) && <span className="status-ok">● complete</span>}
                    {usage.latest_job_status === "complete" && <>{" · "}{lastJobRecords.toLocaleString()} records</>}
                  </span>
                ) : "No backups yet"
              }
              icon="check"
            />
            <StatCard
              accent="blue"
              label="Last backup size"
              value={formatBytes(lastJobBytes)}
              sub={`${connectors.length} connector${connectors.length !== 1 ? "s" : ""}${totalDataTypes > 0 ? ` · ${totalDataTypes} data type${totalDataTypes !== 1 ? "s" : ""}` : ""}`}
              icon="files"
            />
          </div>

          {/* ── Bottom grid ── */}
          <div className="bottom-grid">

            {/* Events / Files table */}
            <div className="card">
              <div className="tab-strip">
                <button
                  className={`tab ${activeTab === "events" ? "active" : ""}`}
                  onClick={() => setActiveTab("events")}
                >
                  Latest events
                </button>
                <button
                  className={`tab ${activeTab === "files" ? "active" : ""}`}
                  onClick={() => setActiveTab("files")}
                >
                  Files
                </button>
              </div>
              {activeTab === "events" ? (
                <EventsTable jobs={jobs} onSelect={setSelectedJob} />
              ) : (
                <FilesTable job={selectedJob ?? lastJob ?? null} />
              )}
            </div>

            {/* Right panel */}
            <div className="side-stack">
              <ConnectorsPanel connectors={connectors} />
              <StoragePanel
                usedBytes={totalBytes}
                schedules={schedules}
              />
            </div>
          </div>
          </>)}
        </main>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0 || !isFinite(bytes)) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function formatCountdown(secs: number): string {
  if (secs <= 0) return "now";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ── Types ─────────────────────────────────────────────────────────────────────
export interface Job {
  id: string;
  status: "queued" | "running" | "complete" | "failed" | "cancelled";
  triggered_by: "schedule" | "manual" | "api";
  data_types: string[];
  total_records: Record<string, number> | null;
  total_bytes: number | null;
  error_message: string | null;
  queued_at: string;
  started_at: string | null;
  completed_at: string | null;
  files?: BackupFile[];
}

export interface BackupFile {
  id: string;
  storage_key: string;
  data_type: string;
  record_count: number;
  size_bytes: number;
}

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  plan: string;
  max_orgs: number;
  features: string[];
  session_timeout_mins?: number;
}

export interface User {
  id: string;
  email: string;
  role: string;
}

export interface Connector {
  id: string;
  connector_type: string;
  display_name: string;
  xero_tenant_id: string | null;
  is_active: boolean;
  token_expires_at: string | null;
}

export interface Usage {
  active_connectors:    number;
  jobs_last_30d:        number;
  total_bytes_all_time: number;
  latest_job_bytes:     number | null;
  latest_job_records:   Record<string, number> | null;
  latest_job_status:    "complete" | "failed" | "running" | "queued" | null;
  latest_job_at:        string | null;
  active_users:         number;
}

export interface Schedule {
  id: string;
  connector_id: string;
  connector_name: string;
  cron_expression: string;
  data_types: string[];
  retention_days: number;
  is_active: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
}
